import Dexie, { type Table } from 'dexie';
import type { GameEvent } from '@/core';
import type {
  GameRecord,
  GameRepository,
  RulebookRecord,
  SavedPlayer,
  SavedTeam,
  SavedTournament,
} from './types';
import { assertStorablePlayer, byJerseyNumber } from './teams';

class HoopscopeDB extends Dexie {
  games!: Table<GameRecord, string>;
  events!: Table<GameEvent, string>;
  teams!: Table<SavedTeam, string>;
  players!: Table<SavedPlayer, string>;
  rulebook!: Table<RulebookRecord, string>;
  tournaments!: Table<SavedTournament, string>;

  constructor() {
    super('hoopscope');
    this.version(1).stores({
      games: 'id, updatedAt, status',
      events: 'id, gameId, [gameId+seq]',
    });
    // v2: saved teams (Phase 1.5.3). Existing tables are unchanged.
    this.version(2).stores({ teams: 'id, updatedAt' });
    // v3: the rulebook PDF added on this device.
    this.version(3).stores({ rulebook: 'id' });
    // v4: tournaments, for match numbers.
    this.version(4).stores({ tournaments: 'id, updatedAt' });
    // v5: players on saved teams (Phase 1.6). New team fields are optional and not indexed.
    this.version(5).stores({ players: 'id, teamId' });
  }
}

let db: HoopscopeDB | null = null;
const getDb = () => (db ??= new HoopscopeDB());

export const dexieRepo: GameRepository = {
  async createGame(record, events) {
    const d = getDb();
    await d.transaction('rw', d.games, d.events, async () => {
      await d.games.put(record);
      await d.events.bulkPut([...events]);
    });
  },
  async appendEvents(gameId, events, summary) {
    const d = getDb();
    await d.transaction('rw', d.games, d.events, async () => {
      await d.events.bulkPut([...events]);
      await d.games.update(gameId, { updatedAt: Date.now(), ...summary });
    });
  },
  async loadEvents(gameId) {
    return getDb()
      .events.where('[gameId+seq]')
      .between([gameId, Dexie.minKey], [gameId, Dexie.maxKey])
      .toArray();
  },
  getGame: (gameId) => getDb().games.get(gameId),
  listGames: (limit = 50) => getDb().games.orderBy('updatedAt').reverse().limit(limit).toArray(),
  async activeGame() {
    return getDb()
      .games.orderBy('updatedAt')
      .reverse()
      .filter((g) => g.status !== 'final')
      .first();
  },
  async deleteGame(gameId) {
    const d = getDb();
    await d.transaction('rw', d.games, d.events, async () => {
      await d.events.where('gameId').equals(gameId).delete();
      await d.games.delete(gameId);
    });
  },
  listTeams: () => getDb().teams.orderBy('updatedAt').reverse().toArray(),
  getTeam: (id) => getDb().teams.get(id),
  async saveTeam(team) {
    await getDb().teams.put(team);
  },
  async saveTeams(teams) {
    const d = getDb();
    await d.transaction('rw', d.teams, () => d.teams.bulkPut([...teams]));
  },
  async deleteTeam(id) {
    const d = getDb();
    await d.transaction('rw', d.teams, d.players, async () => {
      await d.players.where('teamId').equals(id).delete();
      await d.teams.delete(id);
    });
  },
  async listPlayers(teamId) {
    const players = await getDb().players.where('teamId').equals(teamId).toArray();
    return players.sort(byJerseyNumber);
  },
  async savePlayer(player) {
    assertStorablePlayer(player);
    await getDb().players.put(player);
  },
  async savePlayers(players) {
    players.forEach(assertStorablePlayer);
    const d = getDb();
    await d.transaction('rw', d.players, () => d.players.bulkPut([...players]));
  },
  async listAllPlayers() {
    const players = await getDb().players.toArray();
    return players.sort(byJerseyNumber);
  },
  async saveTeamLists(teams, players) {
    players.forEach(assertStorablePlayer);
    const d = getDb();
    await d.transaction('rw', d.teams, d.players, async () => {
      await d.teams.bulkPut([...teams]);
      await d.players.bulkPut([...players]);
    });
  },
  async deletePlayer(id) {
    await getDb().players.delete(id);
  },
  listTournaments: () => getDb().tournaments.orderBy('updatedAt').reverse().toArray(),
  async saveTournament(tournament) {
    await getDb().tournaments.put(tournament);
  },
  getRulebook: () => getDb().rulebook.get('rulebook'),
  async saveRulebook(record) {
    await getDb().rulebook.put(record);
  },
  async deleteRulebook() {
    await getDb().rulebook.delete('rulebook');
  },
};
